/**
 * WhatsApp template preview — renders a TemplateListItem as it will appear in a
 * WhatsApp chat bubble (header media/text, body with sample values, footer,
 * buttons). Uses the shared UI component library.
 */

import { IconExternalLink, IconPhone, IconArrowBackUp, IconFileText, IconPlayerPlay, IconMapPin } from '@tabler/icons-react';
import { Stack, Group, Text, Image, ThemeIcon, Divider } from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import type { TemplateListItem, TemplateDefinitionComponent } from '../dialog360.types';

const comp = (t: TemplateListItem, type: string): TemplateDefinitionComponent | undefined =>
  (t.components || []).find((c) => String(c.type || '').toUpperCase() === type);

function substituteBody(text: string, sample?: string[]): string {
  if (!sample || sample.length === 0) return text;
  let out = text;
  sample.forEach((v, i) => { out = out.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g'), v); });
  return out;
}

function ButtonRow({ b }: { b: NonNullable<TemplateDefinitionComponent['buttons']>[number] }) {
  const icon = b.type === 'URL' ? <IconExternalLink size={16} /> : b.type === 'PHONE_NUMBER' ? <IconPhone size={16} /> : <IconArrowBackUp size={16} />;
  return (
    <Group justify="center" gap={6} py={8} style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <ThemeIcon size="sm" variant="transparent" c="#0a7cff">{icon}</ThemeIcon>
      <Text size="sm" fw={500} c="#0a7cff">{b.text}</Text>
    </Group>
  );
}

export function Dialog360TemplatePreview({ template }: { template: TemplateListItem }) {
  const header = comp(template, 'HEADER');
  const body = comp(template, 'BODY');
  const footer = comp(template, 'FOOTER');
  const buttons = comp(template, 'BUTTONS')?.buttons ?? [];

  const headerHandle = header?.example?.headerHandle?.[0];
  const bodySample = body?.example?.bodyText?.[0];
  const bodyText = substituteBody(body?.text ?? '', bodySample);

  return (
    <div style={{ background: 'var(--mantine-color-default-hover)', padding: 16, borderRadius: 12 }}>
      <Card
        withBorder
        padding={0}
        radius="md"
        style={{ maxWidth: 340, marginInline: 'auto', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }}
      >
        {/* Header */}
        {header && header.format === 'IMAGE' && headerHandle && (
          <Image src={headerHandle} h={170} fit="cover" />
        )}
        {header && header.format === 'VIDEO' && (
          <Group justify="center" h={150} bg="dark.5" c="white"><ThemeIcon size="xl" variant="transparent" c="white"><IconPlayerPlay size={40} /></ThemeIcon></Group>
        )}
        {header && header.format === 'DOCUMENT' && (
          <Group h={64} px="md" bg="gray.1" gap="sm"><ThemeIcon variant="light" color="gray"><IconFileText size={18} /></ThemeIcon><Text size="sm" c="dimmed">Document attachment</Text></Group>
        )}
        {header && header.format === 'LOCATION' && (
          <Group h={64} px="md" bg="gray.1" gap="sm"><ThemeIcon variant="light" color="gray"><IconMapPin size={18} /></ThemeIcon><Text size="sm" c="dimmed">Location</Text></Group>
        )}

        <Stack gap={6} p="sm">
          {header && header.format === 'TEXT' && header.text && <Text size="sm" fw={700}>{header.text}</Text>}
          {bodyText && <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{bodyText}</Text>}
          {footer?.text && <Text size="xs" c="dimmed">{footer.text}</Text>}
          <Text size="9px" c="dimmed" ta="right" mt={2}>12:00</Text>
        </Stack>

        {buttons.length > 0 && (
          <Stack gap={0}>
            {buttons.map((b, i) => <ButtonRow key={i} b={b} />)}
          </Stack>
        )}
      </Card>
      {(body?.example?.bodyText?.[0]?.length ?? 0) > 0 && (
        <>
          <Divider my="sm" label="Preview uses the template's sample values" labelPosition="center" />
        </>
      )}
    </div>
  );
}
